/* Cart: remove a whole Pick-n-Mix bag, and keep checkout locked while the first bag is under its minimum. */
if (!customElements.get('bag-min-guard')) {
  const LOCKED = 'data-bag-locked';
  const apply = () => {
    const unmet = [...document.querySelectorAll('bag-min-guard')].some((g) => g.dataset.unmet === 'true');
    document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach((btn) => {
      if (unmet) {
        btn.setAttribute(LOCKED, '');
        btn.setAttribute('aria-disabled', 'true');
        btn.disabled = true;
      } else if (btn.hasAttribute(LOCKED)) {
        btn.removeAttribute(LOCKED);
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
      }
    });
  };
  customElements.define('bag-min-guard', class extends HTMLElement {
    connectedCallback() { apply(); queueMicrotask(apply); setTimeout(apply, 60); }
    disconnectedCallback() { setTimeout(apply, 60); }
  });
}

if (!window.__bagCardsWired) {
  window.__bagCardsWired = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.bagcard__remove');
    if (!btn) return;
    e.preventDefault();
    const keys = (btn.dataset.bagKeys || '').split(',').filter(Boolean);
    if (!keys.length) return;
    const inDrawer = !!btn.closest('cart-drawer');
    const host = inDrawer ? document.querySelector('cart-drawer-items') : document.querySelector('cart-items');
    if (!host || typeof host.getSectionsToRender !== 'function') return;
    btn.disabled = true;
    btn.textContent = 'Removing…';
    const sections = host.getSectionsToRender();
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    try {
      const res = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ updates, sections: sections.map((s) => s.section), sections_url: window.location.pathname }),
      });
      const state = await res.json();
      if (state.errors || state.status) throw new Error(state.errors || state.description || 'update failed');
      host.classList.toggle('is-empty', state.item_count === 0);
      const drawer = document.querySelector('cart-drawer');
      const footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', state.item_count === 0);
      if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);
      sections.forEach((s) => {
        const root = document.getElementById(s.id);
        if (!root || !state.sections[s.section]) return;
        const target = root.querySelector(s.selector) || root;
        target.innerHTML = host.getSectionInnerHTML(state.sections[s.section], s.selector);
      });
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
    } catch (err) {
      console.error(err);
      btn.disabled = false;
      btn.textContent = 'Remove bag';
    }
  });
}

/* Cart: optional name for each bag's sticker. Saved as the hidden line property _bag_name on every line of the bag.
   Saves when the customer taps "Set name", presses Enter, or leaves the box, and then says what the sticker will read. */
if (!window.__bagNamesWired) {
  window.__bagNamesWired = true;
  // the sticker adds "'s Mix" itself, so cut a typed-in " Mix" and a trailing apostrophe + s ("Tammy's Mix" -> "Tammy")
  const justName = (v) => v.replace(/\s+mix$/i, '').trim().replace(/[^a-z0-9\u00c0-\u024f\s.\-]s$/i, (m, i) => (i > 0 ? '' : m)).trim();
  const clean = (v) => justName((v || '').replace(/[<>&"`\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim()).slice(0, 18);
  const tidy = (n) => (n === n.toUpperCase() || n === n.toLowerCase() ? n.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : n);
  const inFlight = {};
  const rows = (bn) => [...document.querySelectorAll('.bagcard__name-input[data-bag="' + bn + '"]')].map((input) => {
    const box = input.closest('.bagcard__name');
    return { input, btn: box.querySelector('.bagcard__name-btn'), note: box.querySelector('.bagcard__name-saved') };
  });
  const showState = (bn, state, name) => rows(bn).forEach(({ input, btn, note }) => {
    if (state === 'saving') { btn.disabled = true; btn.textContent = 'Saving…'; note.classList.remove('is-set'); note.textContent = ''; return; }
    if (state === 'error') { btn.disabled = false; btn.textContent = 'Try again'; note.classList.remove('is-set'); note.textContent = 'Could not save that name. Please try again.'; return; }
    input.value = name; input.dataset.saved = name; btn.disabled = true;
    if (name) { btn.textContent = 'Saved ✓'; note.classList.add('is-set'); note.textContent = '✓ The sticker will say “' + tidy(name) + '’s Mix”'; }
    else { btn.textContent = 'Set name'; note.classList.remove('is-set'); note.textContent = 'No name set. We will use the name on the delivery address.'; }
  });
  const saveName = (input) => {
    const bn = input.dataset.bag;
    const name = clean(input.value);
    if (name === (input.dataset.saved || '')) { showState(bn, 'done', name); return Promise.resolve(); }
    // one save at a time per bag (tapping the button also leaves the box, which would otherwise start a second save)
    inFlight[bn] = (inFlight[bn] || Promise.resolve()).then(async () => {
      if (name === (input.dataset.saved || '')) return;
      showState(bn, 'saving');
      try {
        let cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        // change.js can reorder lines, so after each change look again for the next line of this bag that still needs the name
        for (let guard = 0; guard < 60; guard++) {
          const item = cart.items.find((it) => it.properties && it.properties._pick_your_mix && it.properties._bag_number === bn && (it.properties._bag_name || '') !== name);
          if (!item) break;
          const props = { ...item.properties };
          if (name) props._bag_name = name; else delete props._bag_name;
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: props }),
          });
          if (!res.ok) throw new Error('change failed');
          cart = await res.json();
        }
        // a line's key changes when its properties change, so give the Remove button the new keys
        const keys = cart.items.filter((it) => it.properties && it.properties._pick_your_mix && it.properties._bag_number === bn).map((it) => it.key).join(',');
        document.querySelectorAll('.bagcard__remove[data-bag="' + bn + '"]').forEach((b) => { b.dataset.bagKeys = keys; });
        showState(bn, 'done', name);
      } catch (err) {
        console.error(err);
        showState(bn, 'error');
      }
    });
    return inFlight[bn];
  };
  // typing wakes the button up
  document.addEventListener('input', (e) => {
    const el = e.target.closest && e.target.closest('.bagcard__name-input');
    if (!el) return;
    const box = el.closest('.bagcard__name'); const btn = box.querySelector('.bagcard__name-btn'); const note = box.querySelector('.bagcard__name-saved');
    const changed = clean(el.value) !== (el.dataset.saved || '');
    btn.disabled = !changed; btn.textContent = 'Set name';
    if (changed) { note.classList.remove('is-set'); note.textContent = 'Tap Set name to save it.'; }
  });
  // capture phase + stopPropagation: the theme's cart script treats every change event inside the cart as a quantity edit
  document.addEventListener('change', (e) => {
    const el = e.target.closest && e.target.closest('.bagcard__name-input');
    if (!el) return;
    e.stopPropagation();
    saveName(el);
  }, true);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.bagcard__name-btn');
    if (!btn) return;
    e.preventDefault();
    const input = btn.closest('.bagcard__name').querySelector('.bagcard__name-input');
    saveName(input);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches && e.target.matches('.bagcard__name-input')) { e.preventDefault(); e.target.blur(); } });
}
